import type {
  AccountTransactionDraft,
  Policy,
  PolicyWithRateLimitInfo,
} from '@mls-wallet/shared-types';
import {
  erc20TransferAbi,
  nativeTokenPseudoAddress,
} from '@mls-wallet/shared-types';
import { decodeFunctionData } from 'viem';
import type { RawTransaction } from './raw-transaction';

export type TransactionPolicyContext = Pick<
  AccountTransactionDraft,
  'multichainAccountId' | 'networkId'
> & { userId: string };

/** Check the parent account and network before selecting or reusing a child policy. */
export function isPolicyAvailableForTransaction(
  policy: Pick<Policy, 'isDeleted' | 'networkId' | 'accounts'>,
  context: Pick<TransactionPolicyContext, 'multichainAccountId' | 'networkId'>
): boolean {
  return (
    !policy.isDeleted &&
    policy.networkId === context.networkId &&
    (policy.accounts.length === 0 ||
      policy.accounts.some(({ id }) => id === context.multichainAccountId))
  );
}

/** Use the same initiator rule in policy selection and completed-child validation. */
export function canUserInitiatePolicy(
  policy: Pick<Policy, 'initiatorSetting'>,
  userId: string
): boolean {
  const initiatorSetting = policy.initiatorSetting;
  // Case: an unrestricted initiator setting permits any current member.
  if (!initiatorSetting) {
    return true;
  }

  // Case: an individual initiator setting permits only its selected user.
  if (initiatorSetting.type === 'user') {
    return initiatorSetting.userId === userId;
  }

  return initiatorSetting.members.some((member) => member.id === userId);
}

/**
 * Check the same child payload for JSON import and visual builder previews.
 * The backend remains authoritative for ABI decoding, current policy rules
 * and cumulative usage.
 */
export function getTransactionPolicyError(
  transaction: RawTransaction,
  policy: PolicyWithRateLimitInfo,
  context: TransactionPolicyContext
): string | null {
  // Case: deleted policies or policies outside the parent account/network are ineligible.
  if (!isPolicyAvailableForTransaction(policy, context)) {
    return 'This policy is not available for the selected account and network.';
  }
  // Case: the current user is outside the policy's permitted initiator user or group.
  if (!canUserInitiatePolicy(policy, context.userId)) {
    return 'You cannot initiate transactions with this policy.';
  }
  // Case: exhausted policy capacity blocks another child transaction.
  if (policy.rateLimitInfo.rateLimitReached)
    return 'This policy has reached its rate limit.';
  // Case: an unrestricted transaction policy needs no payload-specific preview checks.
  if (policy.transactionType === 'ANY') return null;

  // Case: contract policies constrain the child destination and function selector.
  if (policy.transactionType === 'CONTRACT_INTERACTION') {
    // Case: empty calldata represents a native send and needs a transfer-capable policy.
    if (transaction.data === '0x')
      return 'Choose a transfer policy for this native send.';
    const conditions = policy.contractInteractionCondition ?? [];
    // Case: no configured contract conditions means the policy permits any contract call.
    if (conditions.length === 0) return null;
    const condition = conditions.find(
      ({ contractAddress }) =>
        contractAddress.toLowerCase() === transaction.to.toLowerCase()
    );
    // Case: the child destination is absent from the policy's allowed contracts.
    if (!condition) return 'This destination is not allowed by the policy.';
    const functions = [
      ...condition.proxyFunctions,
      ...condition.implementationFunctions,
    ];
    // Case: a restricted contract policy does not allow the child function selector.
    if (
      functions.length > 0 &&
      !functions.some(
        ({ selector }) =>
          selector.toLowerCase() === transaction.data.slice(0, 10).toLowerCase()
      )
    ) {
      return 'This function is not allowed by the policy.';
    }
    return null;
  }

  let tokenAddress = nativeTokenPseudoAddress;
  let destination = transaction.to;
  let amount = BigInt(transaction.value);
  // Case: calldata under a transfer policy must describe an ERC-20 transfer.
  if (transaction.data !== '0x') {
    // Case: decode the actual token recipient and amount; unsupported calldata makes the policy ineligible.
    try {
      const decoded = decodeFunctionData({
        abi: erc20TransferAbi,
        data: transaction.data as `0x${string}`,
      });
      // Case: one token-transfer policy cannot also authorize an attached native transfer.
      if (BigInt(transaction.value) !== 0n)
        return 'A token-transfer policy cannot include an additional native send.';
      [destination, amount] = decoded.args;
      tokenAddress = transaction.to as `0x${string}`;
    } catch {
      return 'This transaction is not a supported token transfer.';
    }
  }
  const condition = policy.tokenTransferCondition;
  // Case: the native or ERC-20 asset differs from the token allowed by this policy.
  if (
    condition?.tokenAddress &&
    condition.tokenAddress.toLowerCase() !== tokenAddress.toLowerCase()
  ) {
    return 'This token is not allowed by the policy.';
  }
  // Case: the decoded transfer recipient is outside the policy's destination list.
  if (
    condition?.destinationAddresses.length &&
    !condition.destinationAddresses.some(
      (address) => address.toLowerCase() === destination.toLowerCase()
    )
  ) {
    return 'This recipient is not allowed by the policy.';
  }
  // Case: the child amount exceeds the policy's transfer amount threshold.
  if (
    condition?.amountThreshold &&
    amount > BigInt(condition.amountThreshold)
  ) {
    return 'This amount exceeds the policy limit.';
  }
  return null;
}
